// @ts-nocheck
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/20/solid';
import axios from 'axios';
import jwtDecode from 'jwt-decode';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { URL } from '../../service/Service';
import { UserContext } from './UserContext';
import AuthShell from '../../components/auth/AuthShell';
import { useTranslation } from '../../i18n/LanguageContext';

export default function Login() {
  const { t } = useTranslation();
  const [userEmail, setUserEmail] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { setUser } = useContext(UserContext);

  useEffect(() => {
    const email = localStorage.getItem('email');

    if (email) {
      setUserEmail(email);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      const loginResponse = await axios.post(`${URL}/api/authenticate`, {
        userEmail,
        userPassword,
      });

      const decoded = jwtDecode(loginResponse.data);
      const role = decoded.authorities[0].authority;

      sessionStorage.setItem('userRole', role);
      sessionStorage.setItem('jwt', loginResponse.data);

      if (rememberMe) {
        localStorage.setItem('email', userEmail);
      } else {
        localStorage.removeItem('email');
      }

      setUser(decoded);

      if (role === 'ADMIN' || role === 'MANAGER') {
        navigate('/admin/dashboard');
      } else if (role === 'PARTNER') {
        navigate('/user/dashboard');
      }
    } catch (error) {
      if (error.response) {
        const { status, data } = error.response;

        if (status === 400 && String(data).includes('Password expired')) {
          setError('Password expirada. Foi enviado um email para atualizar a sua password.');

          setTimeout(() => {
            navigate('/resetpassword');
          }, 5000);
        } else if (status === 401 || status === 400) {
          setError('Email ou password inválida.');
        } else {
          setError(`Erro inesperado: ${data || 'Tente novamente mais tarde.'}`);
        }
      } else {
        setError('Erro de conexão. Verifique a sua ligação.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
      <AuthShell
          title={t('login.title')}
          description={t('login.description')}
          backHref="/"
          backLabel={t('login.backHome')}
      >
            <form className="space-y-5" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-900">
                  {t('login.email')}
                </label>

                <div className="mt-2">
                  <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={userEmail}
                      onChange={(event) => setUserEmail(event.target.value)}
                    className="ui-input"
                      placeholder="exemplo@dominio.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-gray-900">
                  {t('login.password')}
                </label>

                <div className="relative mt-2">
                  <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      required
                      value={userPassword}
                      onChange={(event) => setUserPassword(event.target.value)}
                      className="ui-input pr-12"
                      placeholder={t('login.passwordPlaceholder')}
                  />

                  <button
                      type="button"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => setShowPassword((value) => !value)}
                      className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-xl text-green-cl transition hover:bg-green-cl/5 hover:text-green-900 focus:outline-none"
                      aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                      title={showPassword ? t('login.hidePassword') : t('login.showPassword')}
                  >
                    {showPassword ? (
                        <EyeIcon className="h-5 w-5" aria-hidden="true" />
                    ) : (
                        <EyeSlashIcon className="h-5 w-5" aria-hidden="true" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between gap-3">
                <label className="flex cursor-pointer items-center">
                  <input
                      id="remember_me"
                      type="checkbox"
                      className="h-4 w-4 rounded border-green-cl/60 text-green-cl shadow-sm focus:ring-green-cl"
                      onChange={() => setRememberMe((value) => !value)}
                      checked={rememberMe}
                  />

                  <span className="ml-2 block text-sm text-gray-900">
                  {t('login.remember')}
                </span>
                </label>

                <a
                    href="/resetpassword"
                    className="text-sm font-semibold text-green-cl transition hover:text-green-900 hover:underline focus:outline-none"
                >
                  {t('login.forgot')}
                </a>
              </div>

              <button
                  type="submit"
                  disabled={loading}
                  className="ui-button-primary w-full"
              >
                {loading ? (
                    <div className="flex items-center">
                      <svg
                          className="mr-2 h-5 w-5 animate-spin text-green-cl"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                      >
                        <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                        />

                        <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>

                      {t('login.submitting')}
                    </div>
                ) : (
                    t('login.submit')
                )}
              </button>

              {error && (
                  <div className="ui-alert-error justify-center text-center" role="alert">
                    {error}
                  </div>
              )}
            </form>
      </AuthShell>
  );
}
